import { ForwardRefExoticComponent, HTMLAttributes, ReactNode, RefAttributes, forwardRef } from 'react';
import { combineClassNames } from '../../Utils/ClassnameUtils';

export type GWPSelectionDockPlacement = 'bottom-end' | 'bottom-center';

export interface GWPSelectionDockProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	count: ReactNode;
	label?: ReactNode;
	actions?: ReactNode;
	trailing?: ReactNode;
	placement?: GWPSelectionDockPlacement;
}

export interface GWPSelectionDockDividerProps extends HTMLAttributes<HTMLSpanElement> {}

export const GWPSelectionDockDivider = forwardRef<HTMLSpanElement, GWPSelectionDockDividerProps>((props, ref) => {
	const { className, ...rest } = props;

	return <span ref={ref} className={combineClassNames('gwp-selection-dock-divider', className)} aria-hidden="true" {...rest} />;
});

GWPSelectionDockDivider.displayName = 'GWPSelectionDockDivider';

type GWPSelectionDockComponent = ForwardRefExoticComponent<GWPSelectionDockProps & RefAttributes<HTMLDivElement>> & {
	Divider: typeof GWPSelectionDockDivider;
};

const GWPSelectionDockRoot = forwardRef<HTMLDivElement, GWPSelectionDockProps>((props, ref) => {
	const {
		count,
		label = 'selected',
		actions,
		trailing,
		placement = 'bottom-end',
		className,
		role = 'toolbar',
		['aria-label']: ariaLabel = 'Selection actions',
		...rest
	} = props;

	const hasMiddleActions = actions !== undefined && actions !== null;
	const hasTrailingActions = trailing !== undefined && trailing !== null;
	const hasLeadingDivider = hasMiddleActions || hasTrailingActions;
	const hasTrailingDivider = hasMiddleActions && hasTrailingActions;

	return (
		<div
			ref={ref}
			className={combineClassNames('gwp-selection-dock', placement, className)}
			{...rest}
		>
			<div className="gwp-selection-dock-surface" role={role} aria-label={ariaLabel}>
				<div className="gwp-selection-dock-section summary">
					<span className="gwp-selection-dock-count">{count}</span>
					{label ? <span className="gwp-selection-dock-label">{label}</span> : null}
				</div>

				{hasLeadingDivider ? <GWPSelectionDockDivider /> : null}

				{hasMiddleActions ? <div className="gwp-selection-dock-section actions">{actions}</div> : null}

				{hasTrailingDivider ? <GWPSelectionDockDivider className="trailing" /> : null}

				{hasTrailingActions ? <div className="gwp-selection-dock-section trailing">{trailing}</div> : null}
			</div>
		</div>
	);
});

GWPSelectionDockRoot.displayName = 'GWPSelectionDock';

const GWPSelectionDock = Object.assign(GWPSelectionDockRoot, {
	Divider: GWPSelectionDockDivider,
}) as GWPSelectionDockComponent;

export default GWPSelectionDock;
